/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { forwardRef, Fragment, useContext, useEffect, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import TurndownService from 'turndown';
import MarkdownIt from 'markdown-it/index.js';

// Component imports
import { Button, Form, Row, Space, Upload } from 'antd';
import SecondaryLanguageAlert from '../UI/SecondaryLanguageAlert';
import AnalyticTagsNodeForm from './Nodes/AnalyticTags/AnalyticTagsNodeForm';
import CallbackNodeForm from './Nodes/Callback/CallbackNodeForm';
import ChannelActionNodeForm from './Nodes/ChannelAction/ChannelActionNodeForm';
import DynamicMultiOptionNodeForm from './Nodes/DynamicMultiOption/DynamicMultiOptionNodeForm';
import EmailNodeForm from './Nodes/Email/EmailNodeForm';
import FileUploadNodeForm from './Nodes/FileUpload/FileUploadNodeForm';
import HttpRequestNodeForm from './Nodes/HttpRequest/HttpRequestNodeForm';
import IntroNodeForm from './Nodes/Intro/IntroNodeForm';
import JumpNodeForm from './Nodes/Jump/JumpNodeForm';
import LiveChatNodeForm from './Nodes/LiveChat/LiveChatNodeForm';
import MediaNodeForm from './Nodes/Media/MediaNodeForm';
import MessageNodeForm from './Nodes/Message/MessageNodeForm';
import MultiOptionNodeForm from './Nodes/MultiOption/MultiOptionNodeForm';
import NlpFlowQueryNodeForm from './Nodes/NlpFlowQuery/NlpFlowQueryNodeForm';
import NlpFlowPipelineNodeForm from './Nodes/NlpFlowPipeline/NlpFlowPipelineNodeForm';
import OptionNodeForm from './Nodes/Option/OptionNodeForm';
import SelectNodeForm from './Nodes/Select/SelectNodeForm';
import SwitchCaseNodeForm from './Nodes/SwitchCase/SwitchCaseNodeForm';
import SwitchQueryNodeForm from './Nodes/SwitchQuery/SwitchQueryNodeForm';
import SwitchVariableNodeForm from './Nodes/SwitchVariable/SwitchVariableNodeForm';
import TextInputNodeForm from './Nodes/TextInput/TextInputNodeForm';
import UserFeedbackNodeForm from './Nodes/UserFeedback/UserFeedbackNodeForm';
import UserInfoNodeForm from './Nodes/UserInfo/UserInfoNodeForm';
import VariableNodeForm from './Nodes/Variable/VariableNodeForm';
import CarouselNodeForm from './Nodes/Carousel/CarouselNodeForm';
import CarouselNodeButtonForm from './Nodes/Carousel/CarouselNodeButtonForm';
import ResetSessionForm from './Nodes/ResetSession/ResetSessionForm';
import UserInputQueryNodeForm from './Nodes/UserInput/UserInputQueryNodeForm';
import LiveChatRequestNodeForm from './Nodes/LiveChat/LiveChatRequestNodeForm';
import LlmNodeForm from './Nodes/LLM/LlmNodeForm';
import CustomResponseNodeForm from './Nodes/CustomResponse/CustomResponseNodeForm';
import CsatNodeForm from './Nodes/CSAT/CsatNodeForm';
import StickyNoteNodeForm from './Nodes/StickyNote/StickyNoteNodeForm';

// Misc imports
import { NODE_TYPES, SWITCH_CASE_CONDITIONS } from '../../constants/flowGraph';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { deserializeLIQE, serializeLIQE } from '../../utils/transform/liqe';
import { GraphContext } from './FlowGraph';
import clone from '../../utils/object/clone';
import generateIdFromContext from '../../utils/string/generateIdFromContext';
import { EMOJI_REGEX } from '../../constants/regex';
import { stripRegex } from '../../utils/string/stripRegex';
import { useModalConfirm } from '../../utils/hooks/useModalConfirm';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';
import SemanticSearchNodeForm from './Nodes/SemanticSearch/SemanticSearchNodeForm';

const md = new MarkdownIt();
const turndownService = new TurndownService();

/**
 * Generate the initial values for the form
 * @param {Object} selectedElement
 * @param {String} currentBotLanguage
 * @returns {Object} the initial values for the form
 */
const generateInitialValues = (selectedElement, currentBotLanguage) => {
  // Create a deep, non-mutable, without references copy of the selected element
  let objectToReturn = clone(selectedElement);
  // For message nodes
  if (objectToReturn?.data?.inlineFeedbackData) {
    objectToReturn.data.enableThumbsFeedback = !!selectedElement.data.inlineFeedbackData;
  }

  // For multi-option nodes
  // Map multiOptions from a dictionary to array (to display it in a form list)
  if (objectToReturn?.data?.multiOptions) {
    for (const language of Object.keys(objectToReturn.data.multiOptions)) {
      objectToReturn.data.multiOptions[language] = Object.entries(objectToReturn.data.multiOptions[language] || {}).map(([key, value]) => {
        return ({ key: key, value: value });
      });
    }
  }

  if(objectToReturn?.data?.email?.body) {
    objectToReturn.data.email.body = turndownService.turndown(objectToReturn?.data?.email?.body);
  }

  // For http request nodes
  // Map httpRequestData.headers from a dictionary to array (to display it in a form list)
  if (objectToReturn?.data?.httpRequestData) {
    objectToReturn.data.httpRequestData.headers = Object.keys(objectToReturn.data.httpRequestData.headers).length
      ? Object.entries(objectToReturn.data.httpRequestData.headers || {}).map(([key, value]) => {
        return ({ key: key, value: value });
      })
      : [];

    objectToReturn.data.httpRequestData.urlSearchParams = [];
    if (objectToReturn.data.httpRequestData.url) {
      try {
        // Attempt to parse as URL
        const urlSearchParams = new URL(objectToReturn.data.httpRequestData.url).searchParams;
        if (urlSearchParams.size) {
          const paramArray = [];
          for (const [key, value] of urlSearchParams) {
            paramArray.push({ key, value });
          }
          objectToReturn.data.httpRequestData.urlSearchParams = paramArray;
        }
      } catch (error) {
        // For plain strings, urlSearchParams remains empty array
        objectToReturn.data.httpRequestData.urlSearchParams = [];
      }
    }
  }

  // For callback nodes
  // Map sendStoredData.headers from a dictionary to array (to display it in a form list)
  if (objectToReturn?.data?.sendStoredData) {
    objectToReturn.data.sendStoredData.headers = Object.entries(objectToReturn.data.sendStoredData.headers || {}).map(([key, value]) => {
      return ({ key: key, value: value });
    });
  }

  // For condition nodes
  // Set a default value if there is none
  if (objectToReturn?.data?.switchCaseData) {
    if (!objectToReturn.data.switchCaseData.condition) {
      objectToReturn.data.switchCaseData.condition = SWITCH_CASE_CONDITIONS.EQUAL;
    }
  }

  // For switch query nodes (new) and flow control nodes within the NLP Flow
  // If there is no query in the current bot language, use the query expression of the primary language as default
  if (objectToReturn?.data?.switchQuery) {
    objectToReturn.data.switchQuery = {
      ...objectToReturn.data.switchQuery,
      [currentBotLanguage]:
      deserializeLIQE(objectToReturn.data.switchQuery[currentBotLanguage])
      || objectToReturn.data.switchQuery[currentBotLanguage]
    };
  }
  if (objectToReturn?.data?.query) {
    objectToReturn.data.query = deserializeLIQE(objectToReturn.data.query) || objectToReturn.data.query;
  }

  // For flow control nodes (legacy)
  // Set the local field value
  if (objectToReturn?.data?.switchVariable) {
    objectToReturn.data.variableName = objectToReturn?.data?.switchVariable;

    if (objectToReturn.data.switchVariable.includes('survey')) {
      // For VARIABLE_TYPES.QUESTION
      objectToReturn.data.variableName = objectToReturn.data.switchVariable.split('.')[1];
    } else if (objectToReturn.data.switchVariable.includes('httpStorage')) {
      // For VARIABLE_TYPES.CALLBACK
      objectToReturn.data.variableName = objectToReturn.data.switchVariable.split('.')[1];
      objectToReturn.data.controlNodePropName = objectToReturn.data.switchVariable.split('.')[2];
    }
  }

  if(objectToReturn?.type === NODE_TYPES.LIVECHAT_REQUEST){
    objectToReturn.data['requestExtras'] = objectToReturn.data['requestExtras'] || '{}';
  }

  if(objectToReturn?.type === NODE_TYPES.CSAT || objectToReturn?.type === NODE_TYPES.INPUT_QUERY) {
    objectToReturn.data = objectToReturn.data || {};
    objectToReturn.data['submitLabel'] = objectToReturn.data['submitLabel'] || {};
    // Set the default value for the current language if it doesn't exist for the submitLabel property
    objectToReturn.data['submitLabel'][currentBotLanguage] = objectToReturn?.data?.['submitLabel']?.[currentBotLanguage] ?? 'Submit';
  }

  if(objectToReturn?.type === NODE_TYPES.INPUT_QUERY){
    objectToReturn.data['skipLabel'] = objectToReturn.data['skipLabel'] || {};
    // Set the default value for the current language if it doesn't exist for the skipLabel property
    objectToReturn.data['skipLabel'][currentBotLanguage] = objectToReturn?.data?.['skipLabel']?.[currentBotLanguage] ?? 'Skip';
  }

  if (objectToReturn?.type === NODE_TYPES.SEMANTIC_SEARCH) {
    objectToReturn.data.maxResults = objectToReturn.data.maxResults ?? 10;
  }

  if (objectToReturn.type === NODE_TYPES.VARIABLE) {
    objectToReturn.data = {
      ...objectToReturn.data,
      variable: {
        ...objectToReturn.data?.variable,
        type: objectToReturn.data?.variable?.type ?? 'SESSION'
      }
    };
  }

  return objectToReturn;
};

/**
 * A form that allows the user to edit a flowgraph node. Can be used in a modal or a popover.
 * @param {Object} selectedElement - The selected element to edit, typically a node
 * @param {Object} metadata - Additional metadata for the form, used for special cases like the carousel node
 * @param {Function} onAfterSubmit - A callback function to execute after the form is submitted
 * @param {Function} onAfterCancel - A callback function to execute after the form is cancelled
*/
const NodeEditForm = forwardRef(({
  selectedElement,
  metadata,
  bodyClassName,
  footerClassName,
  onAfterSubmit = () => {},
  onAfterCancel = () => {}
}, ref) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { onElementsUpdated, closeEditForm } = useContext(GraphContext);
  const { currentBotLanguage, primaryBotLanguage, botLanguages } = useBotLanguage();

  const [canSubmit, setCanSubmit] = useState(false);
  const [, updateState] = useState(); // Hook to force a component re-render

  const { showModalConfirm } = useModalConfirm(canSubmit);

  useImperativeHandle(ref, () => ({
    onBeforeCancel
  }));

  // Whenever the selected element or the form reference changes
  useEffect(() => {
    if (selectedElement) {
      // Trigger a rerender for conditions to take effect
      updateState('');
    } else {
      closeEditForm();
      onAfterCancel();
    }
  }, [selectedElement]);

  useEffect(() => {
    form.resetFields();
  }, [selectedElement, currentBotLanguage]);

  const onBeforeCancel = (action) => {
    showModalConfirm({
      defaultAction: () => {
        return !canSubmit ? action() : null; //in case of changes return null to keep the form visible for the user
      },
      onConfirm: () => {
        action();
        setCanSubmit(false);
      },
      okText: t('Discard and Close'),
      cancelText: t('Cancel'),
      title: t('Unsaved Input'),
      content: t('You have unsaved input in this form. Closing it now will discard all entered data. Are you sure you want to proceed?')
    });
  };

  const onSubmit = () => {
    if (form) {
      try {
        const formValues = form.getFieldsValue(true);
        form.validateFields()
          .then(() => {
            // Width and height introduced problems when changing form values in carousel nodes
            const { width, height, ...valuesToSubmit } = formValues;

            //#region Handle special cases
            // For action channel nodes
            if (selectedElement.type === NODE_TYPES.ACTION) {
              botLanguages.forEach(language => {
                if (!valuesToSubmit?.data?.label[language]) {
                  valuesToSubmit.data.label[language] = '';
                }
              });
            }

            // Convert Markdown to HTML
            if (selectedElement.type === NODE_TYPES.EMAIL) {
              const emailBodyMarkdown = formValues?.data?.email?.body;
              if(emailBodyMarkdown) {
                const emailBodyHTML = md.render(emailBodyMarkdown);
                valuesToSubmit.data.email.body = emailBodyHTML;
              }
            }

            // For multi-option nodes
            if (selectedElement.type === NODE_TYPES.MULTI_OPTION) {
              valuesToSubmit.data.multiOptions = Object.fromEntries(
                Object.entries(form.getFieldValue(['data', 'multiOptions'])).map(([lang, items]) => [
                  lang,
                  items.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {})
                ])
              );

              // If the language that was edited was the primary language, we need to update all other languages as well
              // Only the keys are translatable, the values are not.
              // If a key has been added, add it to all secondary langs too. If a key has been removed, remove it from all secondary langs too.
              if (currentBotLanguage === primaryBotLanguage) {
                botLanguages.forEach(language => {
                  if (language !== primaryBotLanguage) {
                    // Add values that do not exist in the secondary language
                    for (const [key, value] of Object.entries(valuesToSubmit.data.multiOptions[primaryBotLanguage])) {
                      if (!Object.values(valuesToSubmit.data.multiOptions?.[language] || {}).includes(value)) {
                        valuesToSubmit.data.multiOptions = {
                          ...valuesToSubmit.data.multiOptions,
                          [language]: {
                            ...valuesToSubmit.data.multiOptions[language],
                            [key]: value
                          }
                        };
                      }
                    }

                    // Remove values that do not exist in the primary language
                    for (const [key, value] of Object.entries(valuesToSubmit.data.multiOptions[language] || {})) {
                      if (!Object.values(valuesToSubmit.data.multiOptions?.[primaryBotLanguage] || {}).includes(value)) {
                        delete valuesToSubmit.data.multiOptions[language][key];
                      }
                    }
                  }
                });
              }
            }

            // For switch-query nodes and flow control nodes within the NLP Flow
            if (selectedElement.type === NODE_TYPES.SWITCH_QUERY) {
              const convertedQueryInCurrentLang = serializeLIQE(valuesToSubmit.data.switchQuery[currentBotLanguage]) || valuesToSubmit.data.switchQuery[currentBotLanguage];
              valuesToSubmit.data.switchQuery = {
                ...selectedElement.data.switchQuery,
                [currentBotLanguage]: convertedQueryInCurrentLang
              };
            }
            if (selectedElement.type === NODE_TYPES.QUERY) {
              const convertedQueryInCurrentLang = serializeLIQE(valuesToSubmit.data.query) || valuesToSubmit.data.query;
              valuesToSubmit.data.query = convertedQueryInCurrentLang;
            }

            // For carousel nodes
            if (selectedElement.type === NODE_TYPES.CAROUSEL) {
              valuesToSubmit.data.label = {
                ...selectedElement.data.label,
                [currentBotLanguage]: valuesToSubmit.data.label[currentBotLanguage]
              };
            }

            // For callback nodes
            if (selectedElement.type === NODE_TYPES.SEND_STORED_DATA) {
              let newHeaders = {};

              for (let entry of formValues.data.sendStoredData.headers) {
                if (entry?.key && entry?.value) {
                  newHeaders[entry.key] = entry.value;
                } else {
                  newHeaders = { '': '' };
                }
              }
              valuesToSubmit.data.sendStoredData.headers = newHeaders;
            }
            //#endregion

            // For http request nodes
            if (selectedElement.type === NODE_TYPES.HTTP_REQUEST) {
              const newHeaders = {};
              for (const entry of formValues.data.httpRequestData.headers) {
                if (entry?.key && entry?.value) {
                  newHeaders[entry.key] = entry.value;
                }
              }
              valuesToSubmit.data.httpRequestData.headers = newHeaders;
            }

            // For select and input query nodes
            // Generate a question ID if it doesn't exist
            if ((selectedElement.type === NODE_TYPES.SELECT || selectedElement.type === NODE_TYPES.INPUT_QUERY || selectedElement.type === NODE_TYPES.FILE_UPLOAD) && !selectedElement.data.questionId && !valuesToSubmit.data.questionId) {
              valuesToSubmit.data.questionId = generateIdFromContext(stripRegex(valuesToSubmit.data.label[primaryBotLanguage], EMOJI_REGEX));
            }

            // For CSAT nodes
            // Generate a variable name if it doesn't exist (as we did for the question IDs in other nodes)
            if ((selectedElement.type === NODE_TYPES.CSAT) && !selectedElement.data.variableName && !valuesToSubmit.data.variableName) {
              valuesToSubmit.data.variableName = generateIdFromContext(stripRegex(valuesToSubmit.data.label[primaryBotLanguage], EMOJI_REGEX));
            }

            // // For LLM Nodes
            // // Generate a variable name if it doesn't exist
            if((selectedElement.type === NODE_TYPES.LLM) && !selectedElement?.data?.llm?.variable && !valuesToSubmit?.data?.llm?.variable) {
              valuesToSubmit.data.llm.variable = generateIdFromContext('LLM');
            }

            if(selectedElement.type === NODE_TYPES.SEMANTIC_SEARCH && !selectedElement.data.variableName && !valuesToSubmit.data.variableName) {
              valuesToSubmit.data.variableName = generateIdFromContext('semsearch');
            }

            onElementsUpdated([{ [selectedElement.id]: valuesToSubmit }]);
            closeEditForm();
            onAfterSubmit();
          }).catch(errorInfo => {
            if (errorInfo.errorFields && errorInfo.errorFields.length > 0) {
              openConsoleAlertMessage({ type: 'error', message: t('Save failed. Please make sure you entered everything properly and try again.') });
            }
          });
      } catch (errorInfo) {
        openConsoleAlertMessage({ type: 'error', message: t('Save failed. Please make sure you entered everything properly and try again.') });
      }
    }
  };

  const renderFormItems = () => {
    if (!selectedElement) {
      return;
    }

    switch (selectedElement.type) {
      case NODE_TYPES.INTRO:
        return <IntroNodeForm form={form} />;

      case NODE_TYPES.MESSAGE:
        return <MessageNodeForm form={form} selectedElement={selectedElement} />;

      case NODE_TYPES.SELECT:
        return <SelectNodeForm form={form} selectedElement={selectedElement} />;

      case NODE_TYPES.TEXT_INPUT:
        return <TextInputNodeForm form={form} />;

      case NODE_TYPES.OPTION:
      case NODE_TYPES.LINK_OPTION:
        return <OptionNodeForm form={form} selectedElement={selectedElement} />;

      case NODE_TYPES.MULTI_OPTION:
        return <MultiOptionNodeForm form={form} selectedElement={selectedElement} setCanSubmit={setCanSubmit} />;

      case NODE_TYPES.DYNAMIC_MULTI_OPTION:
        return <DynamicMultiOptionNodeForm />;

      case NODE_TYPES.FILE_UPLOAD:
        return <FileUploadNodeForm form={form} />;

      case NODE_TYPES.SWITCH_QUERY:
        return <SwitchQueryNodeForm form={form} selectedElement={selectedElement} />;

      case NODE_TYPES.SWITCH_VARIABLE:
        return <SwitchVariableNodeForm form={form} />;

      case NODE_TYPES.SWITCH_CASE:
        return <SwitchCaseNodeForm form={form} selectedElement={selectedElement} />;

      case NODE_TYPES.SEND_STORED_DATA:
        return <CallbackNodeForm />;

      case NODE_TYPES.HTTP_REQUEST:
        return <HttpRequestNodeForm form={form} />;

      case NODE_TYPES.JUMP:
        return <JumpNodeForm />;

      case NODE_TYPES.VARIABLE:
        return <VariableNodeForm form={form} setCanSubmit={setCanSubmit} />;

      case NODE_TYPES.LIVECHAT:
        return <LiveChatNodeForm form={form} selectedElement={selectedElement} />;

      case NODE_TYPES.LIVECHAT_REQUEST:
        return <LiveChatRequestNodeForm form={form} selectedElement={selectedElement} />;

      case NODE_TYPES.MEDIA:
        return <MediaNodeForm />;

      case NODE_TYPES.EMAIL:
        return <EmailNodeForm />;

      case NODE_TYPES.ANALYTIC_TAGS:
        return <AnalyticTagsNodeForm form={form} />;

      case NODE_TYPES.USER_INFO:
        return <UserInfoNodeForm form={form} />;

      case NODE_TYPES.USER_FEEDBACK:
        return <UserFeedbackNodeForm form={form} selectedElement={selectedElement} />;

      case NODE_TYPES.ACTION:
        return <ChannelActionNodeForm form={form} />;

      case NODE_TYPES.CAROUSEL:
        return metadata?.typeOfForm === 'popover'
          ? <CarouselNodeButtonForm form={form} selectedElement={selectedElement} cardIndex={metadata?.cardIndex} buttonIndex={metadata?.buttonIndex} sourceHandle={metadata?.sourceHandle}/>
          : <CarouselNodeForm form={form} selectedElement={selectedElement} propertyToFocus={metadata?.propertyToFocus} showGrid={metadata?.openedFromTooltip}/>;

      case NODE_TYPES.QUERY:
        return <NlpFlowQueryNodeForm form={form} selectedElement={selectedElement} />;

      case NODE_TYPES.LLM:
        return <LlmNodeForm form={form} setCanSubmit={setCanSubmit} />;

      case NODE_TYPES.PIPELINE:
        return <NlpFlowPipelineNodeForm />;

      case NODE_TYPES.RESET:
        return <ResetSessionForm />;

      case NODE_TYPES.CUSTOM_RESPONSE:
        return <CustomResponseNodeForm form={form} />;

      case NODE_TYPES.INPUT_QUERY:
        return <UserInputQueryNodeForm form={form} selectedElement={selectedElement}/>;

      case NODE_TYPES.CSAT:
        return <CsatNodeForm form={form} />;
      case NODE_TYPES.STICKY_NOTE:
        return <StickyNoteNodeForm form={form} selectedElement={selectedElement}/>;

      case NODE_TYPES.SEMANTIC_SEARCH:
        return <SemanticSearchNodeForm form={form} selectedElement={selectedElement} />;
      default:
        return null;
    }
  };

  const renderFooter = () => {
    return (selectedElement?.type === NODE_TYPES.RESET) ? (
      <Row justify="center">
        <Button
          type="primary"
          size="large"
          onClick={() => form.submit()}
        >
          {t('OK')}
        </Button>
      </Row>
    ) : (
      <Row justify="end">
        <Space>
          {
            selectedElement?.type === NODE_TYPES.MULTI_OPTION ? (
              <Fragment>
                <Upload
                  accept=".csv"
                  showUploadList={false}
                  beforeUpload={file => {
                    /*
                      Example format:
                      key1, value1
                      key2, value2
                    */
                    const reader = new FileReader();
                    reader.onload = e => {
                      let mappedValues = e.target.result.split('\n')
                        .reduce((obj, line) => {
                          // Split each line by comma and trim the values
                          let cols = line.split(',').map(item => item.trim());

                          // If the line has at least 2 columns and both are not empty
                          if (cols.length >= 2 && cols[0].trim().length !== 0 && cols[1].trim().length !== 0) {
                            obj[cols[0]] = cols[1];
                          }

                          return obj;
                        }, {});

                      if (mappedValues && Object.keys(mappedValues).length !== 0) {
                        let options = Object.entries(mappedValues).map(([key, value]) => {
                          return { key: key, value: value };
                        });
                        form.resetFields();
                        form.setFieldsValue({ data: { multiOptions: { [currentBotLanguage]: options } } });
                        setCanSubmit(true);
                      } else if (mappedValues && Object.keys(mappedValues).length === 0) {
                        openConsoleAlertMessage({ type: 'error', message: t('The file doesn\'t contain any values') });
                      }
                    };
                    reader.readAsText(file);
                    return false;
                  }}
                >
                  <Button
                    size="large"
                    type="text"
                  >
                    {t('Load from CSV')}
                  </Button>
                </Upload>

                <Button
                  size="large"
                  type="text"
                  onClick={() => {
                    form.resetFields();
                    form.setFieldsValue({ data: { multiOptions: { [currentBotLanguage]: [{ key: '', value: '' }] } } });
                  }}
                >
                  {t('Clear')}
                </Button>
              </Fragment>
            ) : null
          }

          <Button
            size="large"
            type="text"
            onClick={() => {
              closeEditForm();
              onAfterCancel();
            }}
          >
            {t('Cancel')}
          </Button>

          <Button
            type="primary"
            onClick={() => form.submit()}
            size="large"
            disabled={!canSubmit}
          >
            {t('Done')}
          </Button>
        </Space>
      </Row>
    );
  };

  return (
    <Fragment>
      <div className={bodyClassName}>
        <SecondaryLanguageAlert className="mb-4" />

        <Form
          form={form}
          name="nodeEditForm"
          layout="vertical"
          className="mb-4"
          initialValues={generateInitialValues(selectedElement, currentBotLanguage)}
          requiredMark={false}
          onFinish={onSubmit}
          onFieldsChange={(changedFields, allFields) => {
            // If changes came from form manipulation for backend purposes
            // that also require user input then return
            //(example, empty string set for each language at channelActionNode)
            if(!changedFields.some(field => field.touched)) {
              return;
            }

            setCanSubmit(true);
            updateState(allFields);
          }}
        >
          {renderFormItems()}
        </Form>
      </div>

      <div className={footerClassName}>
        {renderFooter()}
      </div>
    </Fragment>
  );
});

NodeEditForm.propTypes = {
  selectedElement: PropTypes.object,
  metadata: PropTypes.object,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string,
  onAfterSubmit: PropTypes.func,
  onAfterCancel: PropTypes.func
};

export default NodeEditForm;
